// `apps/web` react-query client.
//
// Defaults per `plans/04-build-plan/web-app/waves.md` §Wave 4.1 acceptance:
//  - 1 minute stale time so chatty hooks (e.g. `useMe`) coalesce within a
//    user's interaction window without going stale before they expect it.
//  - Retry only on 5xx (transport / origin faults). 4xx is a contract or
//    auth issue — retrying makes the UI lie about state.
//  - Suspense disabled by default; we adopt it route-by-route in Wave 5.
//
// Exported as a factory so tests can mint isolated clients without the
// in-page singleton bleeding between cases.

import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '@barel/api-client';

const STALE_TIME_MS = 60_000; // 1 minute
const MAX_RETRIES = 2;

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (error instanceof ApiError) {
    return error.httpStatus >= 500;
  }
  // Network-level failures (no envelope parsed) are typically transport faults
  // — retry them up to the cap.
  return true;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: shouldRetry,
      },
      mutations: {
        // Mutations express user intent — retrying silently can double-send.
        retry: false,
      },
    },
  });
}
